export type SceneObjectAction = { label: string; icon?: string; enabled?: boolean; pressed?: boolean; reason?: string | null; slot?: number; children?: () => SceneObjectAction[] | Promise<SceneObjectAction[]>; run: () => void };

/**
 * The wheel's hub identifies WHICH object the actions belong to — a picture reads faster than a
 * clamped caps label when five similar objects sit in one room (owner 2026-09-15). The thumbnail is
 * the object's own art; `initials` is the standee fallback the projection already computes, and
 * `icon` covers structural openings (door / window / curtain) that have no art at all. A submenu
 * keeps the PARENT's hub and only swaps the caption, so drilling into GM Tools never loses the
 * object.
 */
export type SceneObjectHub = { imageUrl?: string | null; initials?: string | null; icon?: string | null };

type RingAction = Pick<SceneObjectAction, 'label' | 'slot'>;
const fixedSlot = (action: RingAction): number | undefined => action.slot
  ?? (action.label === 'Cancel' || action.label === 'Back' ? -1 : undefined);

/** One ring for root and child wheels. Only viewport pressure introduces a More page. */
export function layoutSceneObjectActions(actions: readonly RingAction[], maxDiameter = Infinity): {
  radius: number; size: number; step: number; visibleIndices: number[]; overflowIndices: number[];
  positions: { degrees: number; x: number; y: number }[];
} {
  // Keep the old 44px rim around the centres (including rotated 52px buttons).
  const maxRadius = Math.max(96, (maxDiameter - 88) / 2);
  const capacity = Number.isFinite(maxDiameter) ? Math.max(11, Math.floor(Math.PI / Math.asin(28 / maxRadius))) : actions.length;
  const fixed = actions.flatMap((action, index) => (fixedSlot(action) ?? 0) < 0 ? [index] : []);
  const ordinaryLimit = capacity - fixed.length - 1;
  let ordinaryCount = 0;
  const visibleIndices: number[] = [], overflowIndices: number[] = [];
  actions.forEach((action, index) => {
    if (actions.length <= capacity || (fixedSlot(action) ?? 0) < 0 || ordinaryCount++ < ordinaryLimit) visibleIndices.push(index);
    else overflowIndices.push(index);
  });
  const entries: RingAction[] = visibleIndices.map(index => actions[index]!);
  if (overflowIndices.length) entries.push({ label: 'More' });
  let nextSlot = 0;
  const slots = entries.map(action => fixedSlot(action) ?? nextSlot++);
  const legacyFits = entries.length <= 11 && slots.every((slot, index) => slots.slice(index + 1)
    .every(other => Math.abs(192 * Math.sin((slot - other) * 16 * Math.PI / 180)) >= 52));
  const step = legacyFits ? 32 : 360 / entries.length;
  // Chord spacing, rather than arc length, gives neighbours a real 4px gap.
  const radius = legacyFits ? 96 : Math.max(96, 28 / Math.sin(Math.PI / entries.length));
  const size = 2 * (radius + 44);
  const counterclockwise = entries.map((action, index) => ({ slot: fixedSlot(action) ?? 0, index }))
    .filter(entry => entry.slot < 0).sort((a, b) => b.slot - a.slot);
  let clockwise = 0;
  const positions = entries.map((action, index) => {
    const slot = legacyFits ? slots[index]! : (fixedSlot(action) ?? 0) < 0
      ? -1 - counterclockwise.findIndex(entry => entry.index === index) : clockwise++;
    const degrees = slot * step, angle = (degrees - 90) * Math.PI / 180;
    return { degrees, x: size / 2 + Math.cos(angle) * radius, y: size / 2 + Math.sin(angle) * radius };
  });
  return { radius, size, step, visibleIndices, overflowIndices, positions };
}

/** A scene click chooses an object; only a subsequent action changes its state. */
export class SceneObjectActionMenu {
  private cleanup: (() => void) | null = null;
  private revision = 0;
  get isOpen(): boolean { return this.cleanup !== null; }
  isCurrent(revision: number): boolean { return this.cleanup !== null && this.revision === revision; }

  close(): void { this.cleanup?.(); }

  open(title: string, state: string, client: { x: number; y: number }, actions: SceneObjectAction[], returnFocus: HTMLElement, hub?: SceneObjectHub, feedback = ''): number {
    this.close();
    const revision = ++this.revision;
    const abort = new AbortController();
    const menu = document.createElement('div');
    menu.className = 'ft3d-window-radial ft3d-action-wheel';
    menu.dataset.ft3dDoorActions = '';
    menu.style.zIndex = '31010';
    menu.setAttribute('role', 'menu');
    menu.tabIndex = -1;
    menu.style.outline = 'none';
    menu.setAttribute('aria-label', `${title} actions`);
    const diameter = (): number => Math.min(window.visualViewport?.width ?? window.innerWidth,
      window.visualViewport?.height ?? window.innerHeight) - 24;
    let ring = layoutSceneObjectActions(actions, diameter());
    const visibleActions = ring.visibleIndices.map(index => actions[index]!);
    if (ring.overflowIndices.length) visibleActions.push({ label: 'More', icon: 'info',
      children: () => ring.overflowIndices.map(index => actions[index]!), run: () => {} });
    const placeTabs = (): void => {
      menu.style.setProperty('--ft3d-action-wheel-size', `${ring.size}px`);
      buttons.forEach((button, index) => {
        const point = ring.positions[index]!;
        button.style.setProperty('--ft3d-action-angle', `${point.degrees}deg`);
        button.style.left = `${point.x}px`; button.style.top = `${point.y}px`;
      });
    };
    let priorDiameter = diameter();
    let priorLayout = '';
    const position = (): void => {
      const available = diameter();
      if (available !== priorDiameter) {
        const next = layoutSceneObjectActions(actions, available);
        if (next.visibleIndices.join(',') !== ring.visibleIndices.join(',')) {
          this.open(title, state, client, actions, returnFocus, hub, feedback); return;
        }
        ring = next; priorDiameter = available; placeTabs();
      }
      const viewport = window.visualViewport;
      const left = viewport?.offsetLeft ?? 0, top = viewport?.offsetTop ?? 0;
      const width = viewport?.width ?? window.innerWidth, height = viewport?.height ?? window.innerHeight;
      const half = ring.size / 2, sideExtent = half + 6, upperExtent = half + 30;
      const lowerExtent = half + (feedback ? 70 : 6);
      const marginX = Math.min(sideExtent, width / 2), marginY = Math.min(Math.max(upperExtent, lowerExtent + 4), height / 2);
      const clampX = (x: number): number => Math.max(left + marginX, Math.min(left + width - marginX, x));
      const clampY = (y: number): number => Math.max(top + marginY, Math.min(top + height - marginY, y));
      const obstacles: { left: number; right: number; top: number; bottom: number }[] = [];
      const collect = (doc: Document, dx = 0, dy = 0): void => {
        doc.querySelectorAll<HTMLElement>('[data-ft3d-window], [data-ft3d-minimap], [data-ft3d-actor-dock], [data-ft3d-dice-window]').forEach(el => {
          const rect = el.getBoundingClientRect();
          if (el.hidden || !rect.width || !rect.height || doc.defaultView?.getComputedStyle(el).visibility === 'hidden') return;
          obstacles.push({ left: rect.left - dx, right: rect.right - dx, top: rect.top - dy, bottom: rect.bottom - dy });
        });
      };
      collect(document);
      try {
        if (window.parent !== window) {
          const frame = window.frameElement?.getBoundingClientRect();
          if (frame) collect(window.parent.document, frame.left, frame.top);
        }
      } catch { /* standalone/cross-origin viewer uses local HUD bounds */ }
      const layout = JSON.stringify([left, top, width, height, obstacles]);
      if (layout === priorLayout) return;
      priorLayout = layout;
      const xs = [clampX(client.x), clampX(left + width / 2)];
      const ys = [clampY(client.y), clampY(top + height / 2)];
      for (const rect of obstacles) {
        xs.push(clampX(rect.left - half - 10), clampX(rect.right + half + 10));
        ys.push(clampY(rect.top - lowerExtent - 4), clampY(rect.bottom + upperExtent + 4));
      }
      let best = { x: xs[0], y: ys[0], score: Infinity };
      for (const x of xs) for (const y of ys) {
        const overlap = obstacles.reduce((sum, rect) => sum
          + Math.max(0, Math.min(x + sideExtent, rect.right) - Math.max(x - sideExtent, rect.left))
          * Math.max(0, Math.min(y + lowerExtent, rect.bottom) - Math.max(y - upperExtent, rect.top)), 0);
        const score = overlap * 10000 + Math.hypot(x - client.x, y - client.y);
        if (score < best.score) best = { x, y, score };
      }
      menu.style.left = `${best.x}px`; menu.style.top = `${best.y}px`;
    };
    window.addEventListener('resize', position, { signal: abort.signal });
    window.visualViewport?.addEventListener('resize', position, { signal: abort.signal });
    window.visualViewport?.addEventListener('scroll', position, { signal: abort.signal });
    const core = document.createElement('div'); core.className = 'ft3d-window-radial__core';
    const art = document.createElement('div'); art.className = 'ft3d-window-radial__art';
    const fallback = (): void => {
      art.classList.add('is-fallback');
      art.textContent = '';
      // Picture → initials → glyph. Initials come first because a picture-less object renders as an
      // INITIALS standee in the scene, so the hub matches what was clicked; a structural opening has
      // no initials and drops through to its item-type glyph.
      if (hub?.initials) art.textContent = hub.initials;
      else if (hub?.icon) art.innerHTML = iconMap(hub.icon, 46, 46, false);
    };
    if (hub?.imageUrl) {
      const image = document.createElement('img');
      image.alt = ''; image.decoding = 'async'; image.loading = 'lazy';
      // Art that 404s (a retired CDN picture) must leave the initials/glyph behind, not a broken box.
      image.addEventListener('error', () => { image.remove(); fallback(); }, { once: true });
      image.src = hub.imageUrl;
      art.append(image);
    } else fallback();
    const caption = document.createElement('div'); caption.className = 'ft3d-window-radial__caption';
    const name = document.createElement('small'); name.textContent = title;
    caption.append(name);
    // The state ('Locked', 'Open', 'moves within reach first') was aria-only until the caption band
    // gave it a place to sit; it is the same string the label already described.
    if (state) { const status = document.createElement('em'); status.className = 'ft3d-window-radial__state'; status.textContent = state; caption.append(status); }
    if (state) core.setAttribute('aria-description', state);
    core.append(art, caption); menu.append(core);
    if (feedback) {
      const result = document.createElement('output');
      result.className = 'ft3d-action-wheel__feedback';
      result.setAttribute('role', 'status');
      result.textContent = feedback;
      menu.append(result);
    }
    const tooltip = (window as unknown as { bootstrap?: { Tooltip?: { getOrCreateInstance: (element: Element, options?: Record<string, unknown>) => { show: () => void; hide: () => void } } } }).bootstrap?.Tooltip;
    const buttons = visibleActions.map(action => {
      const button = document.createElement('button'); button.type = 'button';
      button.className = 'ft3d-window-radial__action';
      const description = action.reason ? `${action.label}: ${action.reason}` : action.label;
      // Rounded saddle outline shared with the minimap bezel, without its circular boss.
      button.innerHTML = '<svg class="ft3d-action-wheel__tab" viewBox="0 0 40 30" aria-hidden="true" focusable="false"><path d="M9.53 28.97A100 100 0 0 1 30.47 28.97Q34.43 29.55 35.01 25.59L37.61 7.78Q38.33 2.83 33.38 2.11A127 127 0 0 0 6.62 2.11Q1.67 2.83 2.39 7.78L4.99 25.59Q5.57 29.55 9.53 28.97Z"/></svg>'
        + `<span class="ft3d-action-wheel__icon">${iconMap(action.icon ?? 'info', 26, 26, true, description)}</span>`;
      button.setAttribute('aria-label', action.label);
      if (action.pressed !== undefined) button.setAttribute('aria-pressed', String(action.pressed));
      // Keep iconMap's tooltip metadata, with the entire touch button as its anchor.
      const icon = button.querySelector('[data-bs-toggle="tooltip"]');
      for (const attribute of ['data-bs-toggle', 'data-bs-placement', 'data-bs-original-title']) {
        const value = icon?.getAttribute(attribute);
        if (value) { button.setAttribute(attribute, value); icon?.removeAttribute(attribute); }
      }
      if (action.reason) button.setAttribute('aria-description', action.reason);
      if (action.enabled === false) { button.setAttribute('aria-disabled', 'true'); button.style.opacity = '.5'; }
      button.setAttribute('role', 'menuitem');
      button.addEventListener('click', event => {
        event.stopPropagation();
        if (button.dataset.previewOnly === 'true') {
          tooltip?.getOrCreateInstance(button, { container: menu, placement: 'top', trigger: 'manual', animation: false }).show();
          return;
        }
        if (action.enabled === false) {
          tooltip?.getOrCreateInstance(button, { container: menu, placement: 'top', trigger: 'manual', animation: false }).show();
          return;
        }
        if (action.children) {
          const back: SceneObjectAction = { label: 'Back', icon: 'previous', run: () => { this.open(title, state, client, actions, returnFocus, hub); } };
          const children = action.children();
          if (Array.isArray(children)) { this.open(action.label, '', client, [...children, back], returnFocus, hub); return; }
          // An async submenu (a row fetched on demand) opens at once on a Loading tab and swaps in its
          // actions only while the user is still on it — a closed or navigated wheel is never reopened.
          const placeholder = (label: string, reason: string): SceneObjectAction => ({ label, icon: 'info', enabled: false, reason, run: () => {} });
          const pending = this.open(action.label, '', client, [placeholder('Loading', 'Loading…'), back], returnFocus, hub);
          children.then(
            loaded => { if (this.isCurrent(pending)) this.open(action.label, '', client, [...loaded, back], returnFocus, hub); },
            () => { if (this.isCurrent(pending)) this.open(action.label, '', client, [placeholder('Unavailable', 'These actions could not be loaded'), back], returnFocus, hub); });
          return;
        }
        this.close(); action.run();
      }, { signal: abort.signal });
      menu.append(button); return button;
    });
    this.cleanup = () => { abort.abort(); if (typeof disposeBootstrapTooltipsWithin === 'function') disposeBootstrapTooltipsWithin(menu); menu.remove(); this.cleanup = null; returnFocus.focus({ preventScroll: true }); };
    document.body.append(menu);
    placeTabs(); position();
    // HUD windows can move without a window resize (dragging, minimising, panel transitions).
    let layoutFrame = 0;
    // The obstacle scan (querySelectorAll + getBoundingClientRect + getComputedStyle across BOTH
    // documents) ran every animation frame while a wheel was open — 1.7 s of a 15 s click window
    // in the wooded Annex (CPU profile 2026-09-10). HUD windows move on user gestures, not per
    // frame: sample the layout a few times a second and let the resize/scroll listeners cover the
    // rest.
    const LAYOUT_SAMPLE_MS = 150;
    let lastLayoutSampleAt = 0;
    const trackLayout = (now: number): void => {
      if (abort.signal.aborted) return;
      if (now - lastLayoutSampleAt >= LAYOUT_SAMPLE_MS) { lastLayoutSampleAt = now; position(); }
      if (!abort.signal.aborted) layoutFrame = requestAnimationFrame(trackLayout);
    };
    layoutFrame = requestAnimationFrame(trackLayout);
    abort.signal.addEventListener('abort', () => cancelAnimationFrame(layoutFrame), { once: true });
    buttons.forEach(button => {
      const options = { container: menu, placement: 'top', trigger: 'manual', animation: false };
      let tip = tooltip?.getOrCreateInstance(button, options);
      // Shared transient-UI cleanup may dispose a tooltip while its button stays mounted.
      const show = (): void => { tip = tooltip?.getOrCreateInstance(button, options); tip?.show(); };
      const hide = (): void => { tip?.hide(); };
      button.addEventListener('pointerenter', show, { signal: abort.signal });
      button.addEventListener('pointerleave', event => {
        if (button.dataset.previewOnly !== 'true'
          && (event.pointerType === 'mouse' || button.getAttribute('aria-disabled') !== 'true')) hide();
      }, { signal: abort.signal });
      button.addEventListener('focus', show, { signal: abort.signal });
      button.addEventListener('blur', hide, { signal: abort.signal });
      let hold: number | null = null;
      let press: { x: number; y: number } | null = null;
      const cancelHold = (): void => { if (hold !== null) window.clearTimeout(hold); hold = null; };
      abort.signal.addEventListener('abort', cancelHold, { once: true });
      button.addEventListener('pointerdown', event => {
        cancelHold(); delete button.dataset.previewOnly;
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
        press = { x: event.clientX, y: event.clientY };
        hold = window.setTimeout(() => { hold = null; button.dataset.previewOnly = 'true'; show(); }, 450);
      }, { signal: abort.signal });
      button.addEventListener('pointermove', event => {
        if (press && Math.hypot(event.clientX - press.x, event.clientY - press.y) > 8) cancelHold();
      }, { signal: abort.signal });
      button.addEventListener('pointerup', cancelHold, { signal: abort.signal });
      button.addEventListener('pointercancel', cancelHold, { signal: abort.signal });
      button.addEventListener('pointerleave', cancelHold, { signal: abort.signal });
    });
    document.addEventListener('pointerdown', event => {
      if (menu.contains(event.target as Node)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      // Closing on press must also consume its release click before it reaches the scene.
      const swallow = (click: MouseEvent): void => { click.preventDefault(); click.stopImmediatePropagation(); };
      document.addEventListener('click', swallow, { capture: true, once: true });
      // A held dismissal remains owned for its whole gesture. If the browser cancels the click,
      // the next distinct press retires the guard instead of swallowing that new gesture.
      window.setTimeout(() => document.addEventListener('pointerdown', () => {
        document.removeEventListener('click', swallow, true);
      }, { capture: true, once: true }), 0);
      this.close();
    }, { signal: abort.signal, capture: true });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' || event.key === 'Tab') { event.stopPropagation(); this.close(); return; }
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
        : index < 0 ? (event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? buttons.length - 1 : 0)
        : (index + (event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1) + buttons.length) % buttons.length;
      buttons[next]?.focus();
    }, { signal: abort.signal, capture: true });
    menu.focus({ preventScroll: true });
    return revision;
  }
}
